import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { apiList, apiPatch } from "@/integrations/api/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canRunDepreciation } from "@/lib/permissions";
import { useBranch } from "@/context/BranchContext";
import { inr } from "@/features/finance/format";

export const Route = createFileRoute("/_authenticated/finance/capitalisation")({
  component: CapitalisationPage,
});

type PendingRow = {
  id: string;
  asset_code: string;
  name: string;
  basic_cost: number | null;
  total_capitalised_value: number | null;
  capitalisation_date: string | null;
  capitalisation_status: string;
  capitalisation_remarks: string | null;
  department_name: string | null;
};

function usePendingRows(branchId: string | null) {
  return useQuery({
    queryKey: ["capitalisation-pending", branchId ?? null],
    queryFn: async () => {
      try {
        const params: any = {
          capitalisation_status_in: "Pending,OnHold",
          status__not_in: "Disposed,Condemned",
          ordering: "-created_at",
          limit: 200
        };
        if (branchId) params.branch = branchId;
        
        type Raw = Omit<PendingRow, "department_name"> & { department: { name: string } | null };
        const res = await apiList<Raw>("/api/v1/assets/", params);
        
        return (res.results || []).map((r) => ({
          id: r.id,
          asset_code: r.asset_code,
          name: r.name,
          basic_cost: r.basic_cost,
          total_capitalised_value: r.total_capitalised_value ?? (r as any).capitalised_value ?? null,
          capitalisation_date: r.capitalisation_date,
          capitalisation_status: r.capitalisation_status || "Pending",
          capitalisation_remarks: r.capitalisation_remarks ?? null,
          department_name: r.department?.name ?? null,
        })) as PendingRow[];
      } catch (e) {
        return [];
      }
    },
  });
}

function useCapitaliseAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      capitalised_value: number;
      capitalisation_date: string;
      capitalisation_remarks: string | null;
      status: "Capitalised" | "OnHold" | "Rejected";
    }) => {
      await apiPatch(`/api/v1/assets/${v.id}/`, {
        capitalised_value: v.capitalised_value,
        capitalisation_date: v.capitalisation_date,
        capitalisation_remarks: v.capitalisation_remarks,
        capitalisation_status: v.status
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capitalisation-pending"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["far"] });
    },
  });
}

function CapitalisationPage() {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const canCapitalise = canRunDepreciation(me?.roles ?? []);
  const { data: rows, isLoading } = usePendingRows(currentBranchId);
  const [edit, setEdit] = useState<PendingRow | null>(null);
  const [value, setValue] = useState("");
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const mut = useCapitaliseAsset();

  function open(r: PendingRow) {
    setEdit(r);
    setValue(String(r.total_capitalised_value ?? r.basic_cost ?? 0));
    setDate(r.capitalisation_date ?? new Date().toISOString().slice(0, 10));
    setRemarks("");
  }

  async function submit(status: "Capitalised" | "OnHold" | "Rejected") {
    if (!edit) return;
    const val = Number(value);
    if (status === "Capitalised" && (!val || val <= 0)) {
      toast.error("Capitalised value is required");
      return;
    }
    try {
      await mut.mutateAsync({
        id: edit.id,
        capitalised_value: val,
        capitalisation_date: date,
        capitalisation_remarks: remarks || null,
        status,
      });
      toast.success(`Marked ${status}`);
      setEdit(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Finance Capitalisation</h1>
        <p className="text-sm text-muted-foreground">
          Review pending assets, confirm capitalised value and date. Depreciation runs only after an asset is Capitalised.
        </p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Pending capitalisation</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Asset</TableHead><TableHead>Department</TableHead>
              <TableHead className="text-right">Basic</TableHead>
              <TableHead className="text-right">Capitalised</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead className="w-32 text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
              {!isLoading && (rows ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No pending assets.</TableCell></TableRow>}
              {(rows ?? []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.asset_code}</div></TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.basic_cost != null ? inr(r.basic_cost) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.total_capitalised_value != null ? inr(r.total_capitalised_value) : "—"}</TableCell>
                  <TableCell><Badge variant={r.capitalisation_status === "OnHold" ? "secondary" : "outline"}>{r.capitalisation_status}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.capitalisation_remarks ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={!canCapitalise} onClick={() => open(r)}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Capitalise Asset</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="font-medium">{edit.name}</span>{" "}
                <span className="text-muted-foreground">({edit.asset_code})</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Capitalised Value (₹) *</Label>
                  <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />
                </div>
                <div>
                  <Label>Capitalisation Date *</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => submit("Rejected")} disabled={mut.isPending}>Reject</Button>
            <Button variant="outline" onClick={() => submit("OnHold")} disabled={mut.isPending}>On Hold</Button>
            <Button onClick={() => submit("Capitalised")} disabled={mut.isPending}>Mark Capitalised</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
